
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Button, Table, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { formatPhone } from '../utils/formatPhone';

const ManageTeachers = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [teachers, setTeachers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', password: '', phone_number: '' });
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [error, setError] = useState('');
    const [confirmDeleteTeacher, setConfirmDeleteTeacher] = useState({ show: false, teacher: null });

    useEffect(() => {
        if (!currentUser || currentUser.role !== 'admin') {
            navigate('/login');
        } else {
            fetchTeachers();
        }
    }, [currentUser, navigate]);

    const fetchTeachers = async () => {
        try {
            const response = await fetch('https://markazsync.com/api/teachers', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });
    
            if (!response.ok) {
                throw new Error(`HTTP Error! Status: ${response.status}`);
            }
    
            const data = await response.json();
            console.log('✅ Teachers API Response:', data);
            
            if (Array.isArray(data)) {
                setTeachers(data);
            } else {
                console.error('❌ Expected an array but got:', data);
                setTeachers([]);
            }
        } catch (error) {
            console.error('❌ Error fetching teachers:', error);
            setError('Failed to fetch teachers');
        }
    };
    
    
    

    const handleAddTeacher = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.password) {
            setError('All fields are required');
            return;
        }

        try {
            const response = await fetch('https://markazsync.com/api/teachers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(formData),
            });

            if (response.status === 201) {
                toast.success('Teacher added successfully');
                fetchTeachers();
                setShowModal(false);
                setFormData({ name: '', email: '', password: '' });
            } else {
                setError('Error adding teacher');
            }
        } catch (error) {
            setError('Error adding teacher');
        }
    };

    const handleUpdateTeacher = async (e) => {
        e.preventDefault();
        if (!editingTeacher.name || !editingTeacher.email) {
            setError('All fields are required');
            return;
        }

        try {
            const response = await fetch(`https://markazsync.com/api/teachers/${editingTeacher.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(editingTeacher),
            });

            if (response.status === 200) {
                toast.success('Teacher updated successfully');
                fetchTeachers();
                setEditingTeacher(null);
            } else {
                setError('Error updating teacher');
            }
        } catch (error) {
            setError('Error updating teacher');
        }
    };

    const handleDeleteTeacher = async (id) => {
        try {
            const response = await fetch(`https://markazsync.com/api/teachers/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            if (response.status === 200) {
                toast.success('Teacher removed successfully');
                fetchTeachers();
            } else {
                setError('Error removing teacher');
            }
        } catch (error) {
            setError('Error removing teacher');
        }
    };

    return (
        <Container className="mt-5">
            <Card className="p-4 shadow-lg">
                <h2 className="text-center text-primary">Manage Teachers</h2>

                <Button variant="success" className="mb-3" onClick={() => setShowModal(true)}>
                    Add New Teacher
                </Button>

                {error && <p className="text-danger">{error}</p>}

                <Table striped bordered hover>
    <thead>
        <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Actions</th>
        </tr>
    </thead>
    <tbody>
        {teachers.map((teacher) => (
            <tr key={teacher.id}>
                <td>{teacher.first_name}</td>
                <td>{teacher.last_name}</td>
                <td>{teacher.email}</td>
                <td>{formatPhone(teacher.phone_number) || 'N/A'}</td>
                <td>
                    <Button variant="warning" size="sm" onClick={() => setEditingTeacher(teacher)}>
                        Edit
                    </Button>{' '}
                    <Button variant="danger" size="sm" onClick={() => setConfirmDeleteTeacher({ show: true, teacher })}>
                        Remove
                    </Button>
                </td>
            </tr>
        ))}
    </tbody>
</Table>

            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
    <Modal.Header closeButton>
        <Modal.Title>{editingTeacher ? 'Edit Teacher' : 'Add Teacher'}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
        <Form onSubmit={editingTeacher ? handleUpdateTeacher : handleAddTeacher}>
            {/* First Name */}
            <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control 
                    type="text" 
                    placeholder="Enter first name" 
                    value={editingTeacher ? editingTeacher.first_name : formData.first_name} 
                    onChange={(e) => editingTeacher 
                        ? setEditingTeacher({ ...editingTeacher, first_name: e.target.value }) 
                        : setFormData({ ...formData, first_name: e.target.value })
                    } 
                    required 
                />
            </Form.Group>

            {/* Last Name */}
            <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control 
                    type="text" 
                    placeholder="Enter last name" 
                    value={editingTeacher ? editingTeacher.last_name : formData.last_name} 
                    onChange={(e) => editingTeacher 
                        ? setEditingTeacher({ ...editingTeacher, last_name: e.target.value }) 
                        : setFormData({ ...formData, last_name: e.target.value })
                    } 
                    required 
                />
            </Form.Group>

            {/* Email */}
            <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control 
                    type="email" 
                    placeholder="Enter email" 
                    value={editingTeacher ? editingTeacher.email : formData.email} 
                    onChange={(e) => editingTeacher 
                        ? setEditingTeacher({ ...editingTeacher, email: e.target.value }) 
                        : setFormData({ ...formData, email: e.target.value })
                    } 
                    required 
                />
            </Form.Group>

            {/* Phone Number */}
            <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control 
                    type="text" 
                    placeholder="Enter phone number" 
                    value={editingTeacher ? editingTeacher.phone_number || '' : formData.phone_number || ''} 
                    onChange={(e) => editingTeacher 
                        ? setEditingTeacher({ ...editingTeacher, phone_number: formatPhone(e.target.value) }) 
                        : setFormData({ ...formData, phone_number: formatPhone(e.target.value) })
                    } 
                />
            </Form.Group>

            {/* Submit Button */}
            <Button variant="primary" type="submit">
                {editingTeacher ? 'Update Teacher' : 'Add Teacher'}
            </Button>
        </Form>
    </Modal.Body>
            </Modal>

            <Modal
                show={confirmDeleteTeacher.show}
                onHide={() => setConfirmDeleteTeacher({ show: false, teacher: null })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Teacher</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {`Remove ${confirmDeleteTeacher.teacher?.first_name || ''} ${confirmDeleteTeacher.teacher?.last_name || ''}`.trim() || 'Remove this teacher?'}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmDeleteTeacher({ show: false, teacher: null })}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={async () => {
                            await handleDeleteTeacher(confirmDeleteTeacher.teacher.id);
                            setConfirmDeleteTeacher({ show: false, teacher: null });
                        }}
                    >
                        Delete Teacher
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
};

export default ManageTeachers;
